import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useEmailVerification } from "../../hooks/useEmailVerification";
import { useResendTimer } from "../../hooks/useResendTimer";
import { resendVerificationEmail } from "../../api/authApi";
import VerifyCodeInput from "../Authentication/VerifyCodeInput";

const VerifiedSubmitButton = ({
  children,
  onClick,
  disabled = false,
  className = "",
  type = "submit",
  verificationMessage = "Please verify your email to perform this action",
  ...props
}) => {
  const { isEmailVerified, email } = useEmailVerification();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  // Whether a code has been sent in this modal session — gates the reveal of the
  // 6-cell input. The code is sent only via an explicit "Send code" click, not on
  // open, since this generic gate carries no inherent "I want to verify now" intent.
  const [codeSent, setCodeSent] = useState(false);
  const [sendError, setSendError] = useState(null);
  const buttonRef = useRef(null);
  const { startCooldown } = useResendTimer(120, "verifyCodeResendTimer:submitGate");

  const { mutate: sendCode, isPending: isSending } = useMutation({
    mutationFn: () => {
      // Guard against firing with email: undefined (user not yet loaded), which
      // would 500 the backend instead of sending a code.
      if (!email) {
        throw new Error("NO_EMAIL");
      }
      return resendVerificationEmail({ email });
    },
    onSuccess: () => {
      setSendError(null);
      startCooldown();
      setCodeSent(true);
    },
    onError: () => {
      setSendError("Couldn't send a code. Please try again.");
    },
  });

  const openModal = () => {
    setCodeSent(false);
    setSendError(null);
    setShowVerificationModal(true);
  };

  const closeModal = () => {
    setShowVerificationModal(false);
    setCodeSent(false);
    setSendError(null);
  };

  const handleClick = (e) => {
    if (!isEmailVerified) {
      e.preventDefault();
      openModal();
      return;
    }
    onClick?.(e);
  };

  // After verifying in the modal, continue the originally-gated action so the user
  // doesn't have to click twice. Submit buttons re-trigger their form; button-type
  // buttons re-run their onClick handler.
  const handleVerifySuccess = () => {
    closeModal();
    if (type === "submit") {
      const form = buttonRef.current?.form;
      if (form?.requestSubmit) {
        form.requestSubmit();
        return;
      }
    }
    onClick?.();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type={type}
        onClick={handleClick}
        disabled={disabled}
        className={`${className} ${!isEmailVerified ? "relative" : ""}`}
        {...props}
      >
        {children}
        {!isEmailVerified && (
          <div className="absolute inset-0 flex items-center justify-center bg-ink-900 bg-opacity-50 rounded-md opacity-0 hover:opacity-100 transition-opacity duration-200">
            <div className="bg-ink-900 text-white text-xs rounded px-2 py-1 flex items-center space-x-1 shadow-lg">
              <ExclamationTriangleIcon className="h-3 w-3" />
              <span>Verification required</span>
            </div>
          </div>
        )}
      </button>

      {/* Verification Modal — inline 6-digit code entry */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-ink-900/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lift rounded-card bg-white">
            <div className="mt-3">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-signal-amber-100">
                  <ExclamationTriangleIcon className="h-6 w-6 text-signal-amber-600" />
                </div>
                <h3 className="text-lg font-medium text-ink-900 mt-4">
                  Email Verification Required
                </h3>
                <p className="text-sm text-ink-500 mt-2">{verificationMessage}</p>
              </div>

              <div className="mt-4">
                {codeSent ? (
                  <VerifyCodeInput
                    email={email}
                    initialSent
                    cooldownKey="verifyCodeResendTimer:submitGate"
                    label="Enter the 6-digit code we emailed you"
                    onSuccess={handleVerifySuccess}
                  />
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-ink-500 mb-3">
                      We'll send a 6-digit code to{" "}
                      <span className="font-medium text-ink-700">{email}</span>.
                    </p>
                    <button
                      type="button"
                      onClick={() => sendCode()}
                      disabled={isSending}
                      className="w-full inline-flex items-center justify-center rounded-pill bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-400 disabled:opacity-50 transition-colors"
                    >
                      {isSending ? "Sending…" : "Send code"}
                    </button>
                    {sendError && (
                      <p className="mt-2 text-sm text-signal-red-600" role="alert">
                        {sendError}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={closeModal}
                className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-ink-200 text-sm font-medium rounded-md text-ink-700 bg-white hover:bg-cream-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
              >
                Continue exploring
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VerifiedSubmitButton;
